import React from "react";
import { gql } from "@apollo/client";
import { useOrgSftpIntegrationSidebarQuery } from "./OrgSftpIntegrationDetailSidebar.generated";

import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import { format, parseISO } from "date-fns";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import GearIcon from "@src/lib/icons/18px/gear-outline";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import ClickableLinkText from "@src/lib/ui/ClickableLinkText";
import Link from "next/link";
import { cn } from "@src/lib/utils/cn";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";
import Table, { FuseSearchConfig } from "@src/lib/ui/Table";
import { useMemo } from "react";

type Props = {
  rowId: string;
  showHeader?: boolean;
};

const OrgSftpIntegrationDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const { _toggleSidebar } = useDetailSidebarContext();
  const { data } = useOrgSftpIntegrationSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });

  const integration = data?.OrgSftpIntegration;

  const tabKeys = useMemo(() => ["details", "remoteCsvFiles"] as const, []);
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = useMemo(
    () => ({
      details: "Details",
      remoteCsvFiles: "Remote CSV files",
    }),
    [],
  );

  const tabCounts: QueryTabCounts<TabKey> = useMemo(() => {
    return {
      remoteCsvFiles: integration?.remoteSftpCsvFiles?.length ?? undefined,
    };
  }, [integration]);

  if (!integration) return null;

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={detailsItems} />,
    remoteCsvFiles: () => {
      if (
        !integration.remoteSftpCsvFiles ||
        integration.remoteSftpCsvFiles.length === 0
      ) {
        return (
          <div className="p-4">
            <div className="text-gray-7">No CSV files found in S3 bucket</div>
          </div>
        );
      }

      type Column = "fileName" | "uploadedAt" | "size";
      const allColumns: Column[] = ["fileName", "uploadedAt", "size"];

      // Create Fuse search config that searches across file name, date, and size
      const fuseSearchConfig: FuseSearchConfig<
        (typeof integration.remoteSftpCsvFiles)[0]
      > = {
        threshold: 0.3,
        keys: [
          {
            name: "fileName",
            getFn: (file) => file.fileName,
          },
          {
            name: "uploadedAt",
            getFn: (file) =>
              file.uploadedAt
                ? format(parseISO(file.uploadedAt), "M/d/yyyy h:mm a")
                : "",
          },
          {
            name: "size",
            getFn: (file) => `${(file.size / 1024).toFixed(2)} KB`,
          },
        ],
      };

      return (
        <Table<(typeof integration.remoteSftpCsvFiles)[0], Column>
          title="Remote CSV files"
          allItems={integration.remoteSftpCsvFiles}
          allColumns={allColumns}
          itemId={(file) => file.key}
          columnTitles={{
            fileName: "File Name",
            uploadedAt: "Uploaded At",
            size: "Size",
          }}
          renderFns={{
            fileName: (file) => file.fileName,
            uploadedAt: (file) =>
              file.uploadedAt
                ? format(parseISO(file.uploadedAt), "M/d/yyyy h:mm a")
                : "-",
            size: (file) => `${(file.size / 1024).toFixed(2)} KB`,
          }}
          showCount={true}
          fuseSearchConfig={fuseSearchConfig}
          columnSelection={{
            enabled: true,
            defaultSelectedColumns: allColumns,
          }}
          sidebarConfig={{
            tableId: "org-sftp-integration-remote-csv-files",
            sidebarType: DetailSidebarType.OrgSftpIntegration,
            enabled: false, // Don't enable sidebar navigation, just use for scoped search
          }}
          noDataEmptyState="No CSV files found in S3 bucket"
          onRowClick={(file, e) => {
            _toggleSidebar(
              e,
              `${rowId}:${encodeURIComponent(file.fileName)}`,
              DetailSidebarType.RemoteHrisSftpCsv,
              "remote-sftp-csv-files",
              false, // Set to false to preserve history and show back button
            );
          }}
        />
      );
    },
  };

  const detailsItems: InfoTableItem[] = [
    {
      label: "Integration ID",
      value: integration.id,
    },
    {
      label: "File path",
      value: integration.filePath || "--",
    },
    {
      label: "Slug",
      value: integration.slug || "--",
    },
    {
      label: "Location group",
      value: integration.locationGroup ? (
        <Link
          href={{
            pathname: "/locations/[id]",
            query: {
              id: integration.locationGroup.id,
            },
          }}
          className={cn("flex items-center no-underline text-[unset]")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ClickableLinkText>
            {integration.locationGroup.name}
          </ClickableLinkText>
        </Link>
      ) : (
        "--"
      ),
    },
    {
      label: "Sync times",
      value:
        integration.syncTimes && integration.syncTimes.length > 0
          ? integration.syncTimes.join(", ")
          : "--",
    },
    {
      label: "Employee ID field",
      value: integration.employeeIdField || "--",
    },
    {
      label: "First name field",
      value: integration.firstNameField || "--",
    },
    {
      label: "Last name field",
      value: integration.lastNameField || "--",
    },
    {
      label: "Phone field",
      value: integration.phoneField || "--",
    },
    {
      label: "Start date field",
      value: integration.startDateField || "--",
    },
    {
      label: "Termination date field",
      value: integration.terminationDateField || "--",
    },
    {
      label: "Location field",
      value: integration.locationField || "--",
    },
    {
      label: "Team field",
      value: integration.teamField || "--",
    },
    {
      label: "Created at",
      value: integration.createdAt
        ? format(parseISO(integration.createdAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Updated at",
      value: integration.updatedAt
        ? format(parseISO(integration.updatedAt), "M/d/yyyy h:mm a")
        : "--",
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={integration.id}
        icon={<GearIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.OrgSftpIntegration}
      />
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts}
            defaultTab="details"
            queryParamKey="org-sftp-integration-tab"
          />
        </div>
      </SidebarContent>
    </div>
  );
};

gql`
  query OrgSftpIntegrationSidebar($id: UUID!) {
    OrgSftpIntegration(id: $id) {
      id
      createdAt
      updatedAt
      filePath
      slug
      syncTimes
      employeeIdField
      firstNameField
      firstNameFallbackField
      lastNameField
      lastNameFallbackField
      phoneField
      phoneFallbackField
      startDateField
      terminationDateField
      locationField
      teamField
      org {
        id
      }
      locationGroup {
        id
        name
      }
      remoteSftpCsvFiles {
        fileName
        key
        uploadedAt
        size
      }
    }
  }
`;

export default OrgSftpIntegrationDetailSidebar;
